import React from 'react';
import { formatSessionDate, isPastSession } from 'react-scale/components/WebinarSessions/helpers';
import _ from 'lodash';
import {
  DataTable,
  Text,
  Skeleton,
  Dialog,
  Flex,
  IconButton,
  Badge,
  Icon,
  Link as RadixLink,
} from '@scale/scaleui-radix';
import { useQuery } from 'react-query';
import { PaginationState, CellContext } from '@tanstack/react-table';
import {
  GetCollectionsSearchParams,
  GetCollectionsSearchSession,
  HydratedUser,
  WebinarStartFilter,
} from '@scale/shared/webinarSessions/types';
import { searchCollections } from 'react-scale/components/WebinarSessions/api';
import WebinarError from 'react-scale/components/WebinarSessions/components/WebinarError';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { faClose, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import CopyButton from 'react-scale/components/WebinarSessions/components/CopyButton';
import path from 'path';

type Props = {
  params: GetCollectionsSearchParams;
  pagination: PaginationState;
  setPagination: (newPagination: PaginationState) => void;
};

type DataTableRow = {
  _id: string;
  name: string;
  sessions: GetCollectionsSearchSession[];
  hosts: HydratedUser[];
  description: string;
};

const NameCell = (props: CellContext<DataTableRow, string>) => {
  const name = props.getValue();
  const _id = props.row.original._id;
  const location = useLocation();

  return (
    <DataTable.CellContentDefault asChild>
      <Flex direction="column" gap="2">
        <RadixLink className="inline-block truncate" color="accent" asChild>
          <RouterLink to={`${path.join(location.pathname, _id)}`}>
            <Text>{name}</Text>
          </RouterLink>
        </RadixLink>
        <Flex gap="1">
          <Text size="1" color="neutral" className="truncate">
            {_id}
          </Text>
          <CopyButton text={_id} label="Webinar ID" size="1" />
        </Flex>
      </Flex>
    </DataTable.CellContentDefault>
  );
};

const MoreSessionsDialog = ({
  sessions,
  startFilter,
}: {
  sessions: GetCollectionsSearchSession[];
  startFilter: WebinarStartFilter;
}) => {
  const numMoreSessions = sessions.length - 1;

  if (numMoreSessions <= 0) {
    return null;
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Badge color="neutral">+{numMoreSessions} more</Badge>
      </Dialog.Trigger>
      <Dialog.Content>
        <Flex justify="between">
          <Dialog.Title>
            {startFilter === WebinarStartFilter.PAST && 'Past sessions'}
            {startFilter === WebinarStartFilter.ALL && 'All sessions'}
            {startFilter === WebinarStartFilter.UPCOMING && 'Upcoming sessions'}
          </Dialog.Title>
          <Dialog.Close>
            <IconButton color="neutral" variant="ghost">
              <Icon icon={faClose} label="Close" size="1" />
            </IconButton>
          </Dialog.Close>
        </Flex>
        <Flex direction="column" gap="2">
          {sessions.map(s => (
            <Flex gap="2" key={s._id}>
              <Text>{formatSessionDate(s.eventStart)}</Text>
              {s.canceled && <Badge color="red">Canceled</Badge>}
            </Flex>
          ))}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const SessionDatesCell = ({
  sessions,
  startFilter,
}: {
  sessions: GetCollectionsSearchSession[];
  startFilter: WebinarStartFilter;
}) => {
  const sortedSessions = sessions.toSorted((a, b) =>
    moment(a.eventStart).diff(moment(b.eventStart)),
  );

  if (sortedSessions.length === 0) {
    // this should never happen
    return <Text>Webinar has no sessions</Text>;
  }

  let displaySessions;
  let displaySessionDate = '';
  if (startFilter === WebinarStartFilter.PAST) {
    const pastSessions = sortedSessions.filter(s => isPastSession(s));
    if (pastSessions.length > 0) {
      // this should be the most recent past session
      displaySessionDate = formatSessionDate(pastSessions[pastSessions.length - 1].eventStart);
    }
    displaySessions = pastSessions;
  } else {
    const futureSessions = sortedSessions.filter(s => !isPastSession(s));

    displaySessionDate =
      futureSessions.length > 0
        ? formatSessionDate(futureSessions[0].eventStart)
        : formatSessionDate(sortedSessions[0].eventStart);
    displaySessions = startFilter === WebinarStartFilter.ALL ? sortedSessions : futureSessions;
  }

  return (
    <DataTable.CellContentDefault asChild>
      <div>
        <div className="truncate">
          <Text>{displaySessionDate}</Text>
        </div>
        <MoreSessionsDialog sessions={displaySessions} startFilter={startFilter} />
      </div>
    </DataTable.CellContentDefault>
  );
};

export const MoreHostsDialog = ({ hosts }: { hosts: HydratedUser[] }) => {
  if (hosts.length === 0) {
    return null;
  }

  return (
    <Dialog.Root>
      {hosts.length - 2 > 0 && (
        <Dialog.Trigger>
          <div>
            <Badge color="neutral">+{hosts.length - 2} more</Badge>
          </div>
        </Dialog.Trigger>
      )}
      <Dialog.Content>
        <Flex justify="between">
          <Dialog.Title>All owners</Dialog.Title>
          <Dialog.Close>
            <IconButton color="neutral" variant="ghost">
              <Icon icon={faClose} label="Close" size="1" />
            </IconButton>
          </Dialog.Close>
        </Flex>
        <Flex direction="column" gap="1">
          {hosts.map(h => (
            <Flex align="center" gap="1" key={h._id}>
              <Icon color="neutral" icon={faEnvelope} size="2" label="host" />
              <Text size="2" color="neutral" className="truncate">
                {h.email}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const HostsCell = (props: CellContext<DataTableRow, HydratedUser[]>) => {
  const hosts = props.getValue();
  return (
    <DataTable.CellContentDefault asChild>
      <Flex direction="column" gap="2">
        {hosts.slice(0, 2).map(host => (
          <div className="flex items-center gap-1" key={host._id}>
            <Icon color="neutral" icon={faEnvelope} size="2" label="host" />
            <Text size="2" color="neutral" className="truncate">
              {host.email}
            </Text>
          </div>
        ))}
        <MoreHostsDialog hosts={hosts} />
      </Flex>
    </DataTable.CellContentDefault>
  );
};

const WebinarCollectionsTable: React.FC<Props> = ({ pagination, setPagination, params }) => {
  const { data, isLoading, error } = useQuery(
    ['webinar-collections-search', params],
    () => searchCollections(params),
    {
      retry: false,
    },
  );

  if (error) {
    return <WebinarError error={error} />;
  }

  const rows: DataTableRow[] = Object.entries(data?.collections ?? {})
    .map(([collectionId, sessions]) => {
      const firstSession = sessions[0];

      const hosts = _.uniqBy(
        sessions.flatMap(s => s.hostedBy),
        '_id',
      );
      return {
        _id: collectionId,
        name: firstSession.name,
        sessions,
        hosts,
        description: firstSession.description,
      };
    })
    .sort((a, b) => {
      const timeSort = params.startFilter === WebinarStartFilter.PAST ? 'DESC' : 'ASC';

      const timeCompare = moment(a.sessions[0].eventStart).diff(moment(b.sessions[0].eventStart));
      if (timeCompare !== 0) return timeSort === 'ASC' ? timeCompare : -timeCompare;

      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;

      return a._id.localeCompare(b._id);
    });

  const columnHelper = DataTable.createColumnHelper<DataTableRow>();

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Webinar',
      enableSorting: true,
      size: 120,
      cell: NameCell,
    }),
    columnHelper.accessor('sessions', {
      id: 'sessions',
      header: 'Sessions',
      enableSorting: true,
      size: 100,
      cell: function SessionDatesCellWrapper(
        props: CellContext<DataTableRow, GetCollectionsSearchSession[]>,
      ) {
        return <SessionDatesCell startFilter={params.startFilter} sessions={props.getValue()} />;
      },
    }),
    columnHelper.accessor('hosts', {
      id: 'hosts',
      header: 'Owners',
      enableSorting: false,
      size: 120,
      cell: HostsCell,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: 'Description',
      enableSorting: false,
    }),
  ];

  return (
    <Skeleton loading={isLoading}>
      <DataTable.Root
        columns={columns}
        data={rows}
        enableColumnFilters={false}
        state={{ pagination }}
        manualPagination
        onPaginationChange={newPagination => {
          setPagination(
            typeof newPagination === 'function' ? newPagination(pagination) : newPagination,
          );
        }}
        rowCount={data?.total || 0}
        className="overflow-auto"
      >
        {({ table }) => (
          <>
            <DataTable.Table>
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header}>
                        {header.column.getCanSort() ? (
                          <DataTable.ColumnHeaderSort header={header} />
                        ) : null}
                      </DataTable.ColumnHeaderCell>
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
            <DataTable.Pagination>
              <DataTable.PaginationPageNavigation />
              <DataTable.PaginationPageSize />
              <DataTable.PaginationTotal />
            </DataTable.Pagination>
          </>
        )}
      </DataTable.Root>
    </Skeleton>
  );
};

export default WebinarCollectionsTable;
